import { useIntegrationsContext } from 'src/providers/IntegrationsProvider/Integrations.context';
import { eIntegrationStatus } from 'src/providers/IntegrationsProvider/Integrations.enums';

/**
 * INT-1516 — the activation verdict, while it is not operational: `pending`
 * while Meta has not activated the number (bff-web's ACTIVATION_PENDING, which
 * also carries any WBS value it does not recognize) and `failed` once the
 * activation window closed on a conclusive "not activated" (ACTIVATION_FAILED).
 * An operational record has no verdict to show: it is CONNECTED.
 */
export type tWapiActivation = 'pending' | 'failed';

const ACTIVATION_BY_STATE: Readonly<Record<string, tWapiActivation>> = {
  [eIntegrationStatus.ACTIVATION_PENDING]: 'pending',
  [eIntegrationStatus.ACTIVATION_FAILED]: 'failed',
};

// The states in which WBS holds an account record for the workspace: GET
// /account answers 200 for all three, so the account-scoped reads have
// something to read.
const ACCOUNT_RECORD_STATES: readonly string[] = [
  eIntegrationStatus.CONNECTED,
  eIntegrationStatus.ACTIVATION_PENDING,
  eIntegrationStatus.ACTIVATION_FAILED,
];

/**
 * The WhatsApp API page gate (INT-1516 KTD9): an account record exists. It used
 * to be CONNECTED, which left a held or failed record's card on its empty state
 * and its notifications unread, although the record, and the notification
 * that tells the customer to reconnect, were right there.
 *
 * Takes the raw string on purpose: the state arrives from bff-web at runtime,
 * and a value this build does not know is not a record (CLAUDE.md, "Runtime
 * Enum Validation for External Input").
 */
export function hasWapiAccountRecord(state: string | undefined): boolean {
  return state !== undefined && ACCOUNT_RECORD_STATES.includes(state);
}

/**
 * The verdict for an integration state, or null when there is none to show
 * (operational, never connected, disconnected, or a value this build does not
 * know). One resolver for every surface that tells held from failed: the page
 * banner, the sidebar pill, the account card, the Alert Center and the chat.
 */
export function resolveWapiActivation(
  state: string | undefined,
): tWapiActivation | null {
  if (state === undefined) return null;
  // Own properties only, the same guard the connect dialog's reason registry
  // uses: "constructor" must not resolve to Object's member.
  return Object.prototype.hasOwnProperty.call(ACTIVATION_BY_STATE, state)
    ? ACTIVATION_BY_STATE[state]
    : null;
}

/**
 * INT-1633 — the connection state the module's analytics report: the three
 * states the module banner tells apart, plus connected. Anything else (never
 * connected, disconnected, a value this build does not know) is not_connected.
 */
export type tWapiConnectionState =
  | 'connected'
  | 'pending'
  | 'failed'
  | 'not_connected';

export function resolveWapiConnectionState(
  state: string | undefined,
): tWapiConnectionState {
  if (state === eIntegrationStatus.CONNECTED) return 'connected';
  return resolveWapiActivation(state) ?? 'not_connected';
}

/**
 * The one criterion of "connected" for the WAPI module (INT-1492 KTD1):
 * the integration's state, the same value the Sidebar and the Dashboard read.
 *
 * - `isConnected`: the account is connected. Every action that sends something
 *   to Meta or to the leads requires it.
 * - `isKnownNotConnected`: the integrations fetch has answered and the account
 *   is not connected. The state defaults to NO_ACCOUNT until that fetch
 *   resolves (Integrations.constants.ts), so `!isConnected` alone would show a
 *   connected workspace its "not connected" empty states for a render. It is
 *   what shows the module, the banner and the drafts to a workspace without an
 *   account (released to every workspace, INT-1493). A held or failed record
 *   is known not connected too: nothing it sends reaches Meta.
 * - `activation` (INT-1516): which verdict a held or failed record carries, so
 *   the banner and the pill say "Meta is confirming" or "reconnect" instead of
 *   "not connected yet". Null until the integrations answer.
 * - `hasAccountRecord` (INT-1516 KTD9): the record exists, so its account data
 *   is coming; the account card waits on a skeleton rather than showing the
 *   "no account" empty state.
 */
export function useWapiNotConnected() {
  const { whatsappBusinessIntegration, isHydrated } = useIntegrationsContext();
  const { state } = whatsappBusinessIntegration;
  const isConnected = state === eIntegrationStatus.CONNECTED;

  const isKnownNotConnected = isHydrated && !isConnected;
  const activation = isHydrated ? resolveWapiActivation(state) : null;
  const hasAccountRecord = hasWapiAccountRecord(state);
  const connectionState = resolveWapiConnectionState(state);

  return {
    isConnected,
    isKnownNotConnected,
    activation,
    hasAccountRecord,
    connectionState,
  };
}
